import { useEffect, useState } from 'react';
import {
    Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../services/api';
import { provinciasAngola } from './dadosAngola';
import RegisterAccess from '../../utils/registerAccess';
import HeaderSession from '../../utils/headerSession';
import TabelaLoading from '../../load/tabelaLoading';
import ListaVazia from '../../utils/listaVazia';

export default function Provincias() {
    const dispatch = useDispatch();
    const [provincias, setProvincias] = useState(useSelector(state => state?.provincias?.data) ?? []);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [designacao, setDesignacao] = useState('');
    const [seeding, setSeeding] = useState(false);
    const [seedFeedback, setSeedFeedback] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get('/provincias')
            .then(res => {
                const lista = res.data.provincias ?? [];
                setProvincias(lista);
                dispatch({ type: 'provincias', payload: { provincias: lista } });
            })
            .finally(() => setLoading(false));
    }, []);

    async function handleSeedAngola() {
        setSeeding(true);
        setSeedFeedback(null);
        let criadas = 0;
        let municipiosCriados = 0;
        let erros = 0;

        for (const prov of provinciasAngola) {
            try {
                const resProv = await api.post('/provincias/create', { designacao: prov.designacao });
                const provinciaId = resProv.data.provincia?._id ?? resProv.data._id;
                criadas++;

                for (const mun of prov.municipios) {
                    try {
                        await api.post('/municipios/create', { designacao: mun, provinciaId });
                        municipiosCriados++;
                    } catch {
                        erros++;
                    }
                }
            } catch {
                erros++;
            }
        }

        // Recarregar lista após seed
        const res = await api.get('/provincias').catch(() => null);
        if (res) {
            const lista = res.data.provincias ?? [];
            setProvincias(lista);
            dispatch({ type: 'provincias', payload: { provincias: lista } });
        }

        setSeedFeedback({
            type: erros === 0 ? 'success' : 'warning',
            message: `${criadas} províncias e ${municipiosCriados} municípios importados.${erros > 0 ? ` (${erros} erros)` : ''}`,
        });
        setSeeding(false);
    }

    function handleOpenDialog() {
        setDesignacao('');
        setFeedback(null);
        setDialogOpen(true);
    }

    function handleClose() {
        if (submitting) return;
        setDialogOpen(false);
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!designacao.trim()) return;
        setSubmitting(true);
        setFeedback(null);

        api.post('/provincias/create', { designacao: designacao.trim() })
            .then(res => {
                const nova = res.data.provincia ?? res.data;
                const lista = [...provincias, nova];
                setProvincias(lista);
                dispatch({ type: 'provincias', payload: { provincias: lista } });
                setFeedback({ type: 'success', message: 'Província registada com sucesso.' });
                setDesignacao('');
                setTimeout(() => setDialogOpen(false), 1200);
            })
            .catch(err => {
                const msg = err.response?.data?.message ?? 'Erro ao registar a província.';
                setFeedback({ type: 'error', message: msg });
            })
            .finally(() => setSubmitting(false));
    }

    return (
        <>
            <RegisterAccess page={'gestão de províncias'} />

            <HeaderSession title='PROVÍNCIAS' />

            {seedFeedback && (
                <Alert severity={seedFeedback.type} sx={{ mb: 2 }} onClose={() => setSeedFeedback(null)}>
                    {seedFeedback.message}
                </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 12 }}>
                <Button
                    variant="outlined"
                    startIcon={<DownloadingIcon />}
                    onClick={handleSeedAngola}
                    disabled={seeding}
                    sx={{ borderColor: '#85287e', color: '#85287e', '&:hover': { borderColor: '#6a1f63', backgroundColor: '#f9f0f9' } }}
                >
                    {seeding ? 'A importar...' : 'Importar Províncias de Angola'}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                    sx={{ backgroundColor: '#85287e', '&:hover': { backgroundColor: '#6a1f63' } }}
                >
                    Adicionar Província
                </Button>
            </div>

            {loading && <TabelaLoading />}

            {!loading && provincias.length === 0 && <ListaVazia text="Nenhuma província registada." />}

            {!loading && provincias.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#dcdede' }}>
                            <TableRow>
                                <TableCell><Typography noWrap><strong>#</strong></Typography></TableCell>
                                <TableCell><Typography noWrap><strong>Designação</strong></Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {provincias.map((prov, idx) => (
                                <TableRow key={prov._id ?? prov.id ?? idx}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{prov.designacao}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Nova Província</DialogTitle>
                    <DialogContent>
                        {feedback && (
                            <Alert severity={feedback.type} sx={{ mb: 2 }}>{feedback.message}</Alert>
                        )}
                        <TextField
                            autoFocus
                            label="Designação"
                            fullWidth
                            required
                            size="small"
                            value={designacao}
                            onChange={e => setDesignacao(e.target.value)}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={submitting}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !designacao.trim()}
                            sx={{ backgroundColor: '#85287e', '&:hover': { backgroundColor: '#6a1f63' } }}
                        >
                            {submitting ? 'A guardar...' : 'Guardar'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
